import { useState, useEffect } from "react";
import { api } from "../lib/api";

interface FundingOpportunity {
  _id?: string;
  id: string;
  name: string;
  provider: string;
  description: string;
  fundingType: string;
  scope: string;
  localAuthorities?: string[];
  regions?: string[];
  postcodeAreas?: string[];
  eligibleCategories?: string[];
  minAmount?: number;
  maxAmount?: number;
  matchFundingRequired?: boolean;
  matchFundingPercentage?: number;
  applicationDeadline?: string;
  status: string;
  isRolling?: boolean;
  applicationUrl?: string;
  contactEmail?: string;
  applicationProcess?: string;
  typicalProcessingTime?: string;
  tags?: string[];
  priority?: number;
  isManual?: boolean;
  aiDiscovered?: boolean;
  lastVerified?: string;
}

const FUNDING_TYPE_OPTIONS = [
  { value: "grant", label: "Grant" },
  { value: "loan", label: "Loan" },
  { value: "tax_relief", label: "Tax Relief" },
  { value: "rate_relief", label: "Rate Relief" },
  { value: "voucher", label: "Voucher/Discount" },
  { value: "subsidy", label: "Subsidy" },
  { value: "match_funding", label: "Match Funding" },
];

const SCOPE_OPTIONS = [
  { value: "local", label: "Local" },
  { value: "regional", label: "Regional" },
  { value: "national", label: "National" },
  { value: "uk_wide", label: "UK-Wide" },
];

const CATEGORY_OPTIONS = [
  { value: "retail", label: "Retail" },
  { value: "food_drink", label: "Food & Drink" },
  { value: "health_beauty", label: "Health & Beauty" },
  { value: "professional_services", label: "Professional Services" },
  { value: "entertainment", label: "Entertainment" },
  { value: "accommodation", label: "Accommodation" },
  { value: "automotive", label: "Automotive" },
  { value: "education", label: "Education" },
  { value: "finance", label: "Finance" },
  { value: "other", label: "Other" },
];

const STATUS_COLORS: Record<string, string> = {
  open: "bg-green-100 text-green-800",
  coming_soon: "bg-blue-100 text-blue-800",
  closing_soon: "bg-orange-100 text-orange-800",
  closed: "bg-gray-100 text-gray-500",
};

const FUNDING_TYPE_ICONS: Record<string, string> = {
  grant: "🎁",
  loan: "🏦",
  tax_relief: "📊",
  rate_relief: "🏠",
  voucher: "🎟️",
  subsidy: "💵",
  match_funding: "🤝",
};

export default function FundingSection() {
  const [opportunities, setOpportunities] = useState<FundingOpportunity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete confirmation
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form fields
  const [formData, setFormData] = useState({
    name: "",
    provider: "",
    description: "",
    fundingType: "grant",
    scope: "local",
    localAuthorities: "",
    regions: "",
    postcodeAreas: "",
    eligibleCategories: [] as string[],
    minAmount: "",
    maxAmount: "",
    matchFundingRequired: false,
    matchFundingPercentage: "",
    applicationDeadline: "",
    isRolling: false,
    applicationUrl: "",
    contactEmail: "",
    applicationProcess: "",
    typicalProcessingTime: "",
    tags: "",
    priority: "5",
  });

  useEffect(() => {
    loadFunding();
  }, []);

  async function loadFunding() {
    setIsLoading(true);
    setError(null);
    try {
      // Use organization-specific endpoint to only show funding for this org
      const response = await api.getOrganizationFunding();
      setOpportunities(response.data.opportunities);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load funding opportunities"
      );
    } finally {
      setIsLoading(false);
    }
  }

  function resetForm() {
    setFormData({
      name: "",
      provider: "",
      description: "",
      fundingType: "grant",
      scope: "local",
      localAuthorities: "",
      regions: "",
      postcodeAreas: "",
      eligibleCategories: [],
      minAmount: "",
      maxAmount: "",
      matchFundingRequired: false,
      matchFundingPercentage: "",
      applicationDeadline: "",
      isRolling: false,
      applicationUrl: "",
      contactEmail: "",
      applicationProcess: "",
      typicalProcessingTime: "",
      tags: "",
      priority: "5",
    });
    setEditingId(null);
    setFormError(null);
  }

  function openCreateForm() {
    resetForm();
    setShowForm(true);
  }

  function openEditForm(opp: FundingOpportunity) {
    setFormData({
      name: opp.name,
      provider: opp.provider,
      description: opp.description,
      fundingType: opp.fundingType,
      scope: opp.scope,
      localAuthorities: opp.localAuthorities?.join(", ") || "",
      regions: opp.regions?.join(", ") || "",
      postcodeAreas: opp.postcodeAreas?.join(", ") || "",
      eligibleCategories: opp.eligibleCategories || [],
      minAmount: opp.minAmount?.toString() || "",
      maxAmount: opp.maxAmount?.toString() || "",
      matchFundingRequired: opp.matchFundingRequired || false,
      matchFundingPercentage: opp.matchFundingPercentage?.toString() || "",
      applicationDeadline: opp.applicationDeadline
        ? new Date(opp.applicationDeadline).toISOString().split("T")[0]
        : "",
      isRolling: opp.isRolling || false,
      applicationUrl: opp.applicationUrl || "",
      contactEmail: opp.contactEmail || "",
      applicationProcess: opp.applicationProcess || "",
      typicalProcessingTime: opp.typicalProcessingTime || "",
      tags: opp.tags?.join(", ") || "",
      priority: opp.priority?.toString() || "5",
    });
    setEditingId(opp.id);
    setShowForm(true);
    setFormError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError(null);

    try {
      const data = {
        name: formData.name.trim(),
        provider: formData.provider.trim(),
        description: formData.description.trim(),
        fundingType: formData.fundingType,
        scope: formData.scope,
        localAuthorities: formData.localAuthorities
          ? formData.localAuthorities
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : undefined,
        regions: formData.regions
          ? formData.regions
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : undefined,
        postcodeAreas: formData.postcodeAreas
          ? formData.postcodeAreas
              .split(",")
              .map((s) => s.trim().toUpperCase())
              .filter(Boolean)
          : undefined,
        eligibleCategories:
          formData.eligibleCategories.length > 0
            ? formData.eligibleCategories
            : undefined,
        minAmount: formData.minAmount
          ? parseInt(formData.minAmount)
          : undefined,
        maxAmount: formData.maxAmount
          ? parseInt(formData.maxAmount)
          : undefined,
        matchFundingRequired: formData.matchFundingRequired,
        matchFundingPercentage: formData.matchFundingPercentage
          ? parseInt(formData.matchFundingPercentage)
          : undefined,
        applicationDeadline: formData.applicationDeadline || undefined,
        isRolling: formData.isRolling,
        applicationUrl: formData.applicationUrl.trim() || undefined,
        contactEmail: formData.contactEmail.trim() || undefined,
        applicationProcess: formData.applicationProcess.trim() || undefined,
        typicalProcessingTime:
          formData.typicalProcessingTime.trim() || undefined,
        tags: formData.tags
          ? formData.tags
              .split(",")
              .map((s) => s.trim().toLowerCase())
              .filter(Boolean)
          : undefined,
        priority: parseInt(formData.priority),
      };

      if (editingId) {
        await api.updateFundingOpportunity(editingId, data);
      } else {
        await api.createFundingOpportunity(data);
      }

      setShowForm(false);
      resetForm();
      loadFunding();
    } catch (err) {
      setFormError(
        err instanceof Error
          ? err.message
          : "Failed to save funding opportunity"
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!deletingId) return;
    setIsDeleting(true);
    try {
      await api.deleteFundingOpportunity(deletingId);
      setDeletingId(null);
      loadFunding();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to delete");
    } finally {
      setIsDeleting(false);
    }
  }

  function formatAmount(amount?: number) {
    if (!amount) return null;
    return `£${amount.toLocaleString()}`;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base md:text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span>💷</span> Funding Opportunities
            </h2>
            <p className="text-xs md:text-sm text-gray-500 mt-1">
              {opportunities.length} opportunit
              {opportunities.length !== 1 ? "ies" : "y"} available
            </p>
          </div>
          <button
            onClick={openCreateForm}
            className="px-3 py-1.5 md:px-4 md:py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors"
          >
            + Add Funding
          </button>
        </div>
        {/* Auto-refresh note */}
        <div className="mt-3 flex items-start gap-2 text-xs text-gray-500 bg-gray-50 rounded-md px-3 py-2">
          <svg
            className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>
            <strong>Note:</strong> National and regional funding opportunities
            are automatically discovered and updated once per month using AI.
            You can manually add local funding opportunities specific to your
            area.
          </span>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="p-8 text-center text-gray-500">
          <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          Loading funding opportunities...
        </div>
      ) : error ? (
        <div className="p-8 text-center text-red-600">{error}</div>
      ) : opportunities.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">💷</span>
          </div>
          <p className="mb-2">No funding opportunities yet.</p>
          <p className="text-sm">
            Click "Add Funding" to add your first local opportunity.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-200 max-h-[500px] overflow-y-auto">
          {opportunities.map((opp) => (
            <li key={opp.id} className="px-4 md:px-6 py-4 hover:bg-gray-50">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-lg">
                      {FUNDING_TYPE_ICONS[opp.fundingType] || "💰"}
                    </span>
                    <h3 className="font-medium text-gray-900 truncate">
                      {opp.name}
                    </h3>
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${
                        STATUS_COLORS[opp.status] || "bg-gray-100"
                      }`}
                    >
                      {opp.status.replace("_", " ")}
                    </span>
                    {opp.isManual && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">
                        Manual
                      </span>
                    )}
                    {opp.aiDiscovered && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                        AI Discovered
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{opp.provider}</p>
                  <p className="text-sm text-gray-500 line-clamp-2">
                    {opp.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
                    {(opp.minAmount || opp.maxAmount) && (
                      <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded">
                        {formatAmount(opp.minAmount)} -{" "}
                        {formatAmount(opp.maxAmount)}
                      </span>
                    )}
                    <span className="bg-gray-100 px-2 py-0.5 rounded capitalize">
                      {opp.scope.replace("_", " ")}
                    </span>
                    {opp.applicationDeadline && (
                      <span className="bg-orange-50 text-orange-700 px-2 py-0.5 rounded">
                        Deadline:{" "}
                        {new Date(opp.applicationDeadline).toLocaleDateString()}
                      </span>
                    )}
                    {opp.isRolling && (
                      <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                        Rolling applications
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {opp.applicationUrl && (
                    <a
                      href={opp.applicationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                      title="View application"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                  )}
                  <button
                    onClick={() => openEditForm(opp)}
                    className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                    title="Edit"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => setDeletingId(opp.id)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    title="Delete"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingId
                  ? "Edit Funding Opportunity"
                  : "Add Funding Opportunity"}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                    placeholder="e.g., High Street Support Grant"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Provider *
                  </label>
                  <input
                    type="text"
                    value={formData.provider}
                    onChange={(e) =>
                      setFormData({ ...formData, provider: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                    placeholder="e.g., Gateshead Council"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={3}
                  required
                  placeholder="Brief description of the funding opportunity..."
                />
              </div>

              {/* Type and Scope */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Funding Type *
                  </label>
                  <select
                    value={formData.fundingType}
                    onChange={(e) =>
                      setFormData({ ...formData, fundingType: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {FUNDING_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scope *
                  </label>
                  <select
                    value={formData.scope}
                    onChange={(e) =>
                      setFormData({ ...formData, scope: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {SCOPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Geographic Targeting */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Local Authorities
                  </label>
                  <input
                    type="text"
                    value={formData.localAuthorities}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        localAuthorities: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Gateshead, Newcastle"
                  />
                  <p className="text-xs text-gray-500 mt-1">Comma-separated</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Regions
                  </label>
                  <input
                    type="text"
                    value={formData.regions}
                    onChange={(e) =>
                      setFormData({ ...formData, regions: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="North East"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postcode Areas
                  </label>
                  <input
                    type="text"
                    value={formData.postcodeAreas}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        postcodeAreas: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="NE, DH, SR"
                  />
                </div>
              </div>

              {/* Eligible Categories */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Eligible Business Categories
                </label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_OPTIONS.map((cat) => (
                    <label
                      key={cat.value}
                      className={`px-3 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                        formData.eligibleCategories.includes(cat.value)
                          ? "bg-primary-100 text-primary-700 border-primary-300"
                          : "bg-gray-100 text-gray-600 border-gray-200"
                      } border`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={formData.eligibleCategories.includes(
                          cat.value
                        )}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              eligibleCategories: [
                                ...formData.eligibleCategories,
                                cat.value,
                              ],
                            });
                          } else {
                            setFormData({
                              ...formData,
                              eligibleCategories:
                                formData.eligibleCategories.filter(
                                  (c) => c !== cat.value
                                ),
                            });
                          }
                        }}
                      />
                      {cat.label}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty for all categories
                </p>
              </div>

              {/* Amounts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Amount (£)
                  </label>
                  <input
                    type="number"
                    value={formData.minAmount}
                    onChange={(e) =>
                      setFormData({ ...formData, minAmount: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="500"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Amount (£)
                  </label>
                  <input
                    type="number"
                    value={formData.maxAmount}
                    onChange={(e) =>
                      setFormData({ ...formData, maxAmount: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="10000"
                    min="0"
                  />
                </div>
              </div>

              {/* Match Funding */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.matchFundingRequired}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        matchFundingRequired: e.target.checked,
                      })
                    }
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">
                    Match funding required
                  </span>
                </label>
                {formData.matchFundingRequired && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={formData.matchFundingPercentage}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          matchFundingPercentage: e.target.value,
                        })
                      }
                      className="w-20 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="50"
                      min="0"
                      max="100"
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                )}
              </div>

              {/* Deadline */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Application Deadline
                  </label>
                  <input
                    type="date"
                    value={formData.applicationDeadline}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        applicationDeadline: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={formData.isRolling}
                  />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isRolling}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          isRolling: e.target.checked,
                          applicationDeadline: "",
                        })
                      }
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">
                      Rolling applications (no deadline)
                    </span>
                  </label>
                </div>
              </div>

              {/* URLs and Contact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Application URL
                  </label>
                  <input
                    type="url"
                    value={formData.applicationUrl}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        applicationUrl: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) =>
                      setFormData({ ...formData, contactEmail: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="grants@council.gov.uk"
                  />
                </div>
              </div>

              {/* Application Process */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Application Process
                </label>
                <textarea
                  value={formData.applicationProcess}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      applicationProcess: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={2}
                  placeholder="Brief description of how to apply..."
                />
              </div>

              {/* Processing Time and Tags */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Typical Processing Time
                  </label>
                  <input
                    type="text"
                    value={formData.typicalProcessingTime}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        typicalProcessingTime: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., 4-6 weeks"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) =>
                      setFormData({ ...formData, tags: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="shopfront, digital, green"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Comma-separated keywords
                  </p>
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority (1-10, higher = more important)
                </label>
                <input
                  type="range"
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                  className="w-full"
                  min="1"
                  max="10"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Low</span>
                  <span className="font-medium">{formData.priority}</span>
                  <span>High</span>
                </div>
              </div>

              {/* Error */}
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {formError}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Delete Funding Opportunity
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this funding opportunity? This
              action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setDeletingId(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
